import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Split from "react-split";
import {
  Box,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { breakpoints } from "../../theme/theme";

// utilities
import { setActivePage } from "../../features/navigation/navigationSlice";
import DEFAULT_ROUTES from "../../constants/routes";
import useQueryParams from "../../utils/useQueryParams";

// components
import "./TestCaseView.css";
import ExportTemplate from "./ExcelTemplate";
import ImportTestCases from "./ImportTestCases";
import PageHeader from "../Header/PageHeader";
import ModuleExplorer from "../explorer/moduleExplorer";
import TestCaseList from "./TestCaseList";
import GlobalActionButtons from "../Button/GlobalActionButtons";
import FolderDrawerTreeView from "../FolderDrawerTreeView";
import { bulkCreateTestCaseApiCall, createModuleApiCall, deleteModuleApiCall, editModuleApiCall, fetchModulesTreeViewData, fetchModulesWithCount } from "../../utils/api";
import { getAllLabels } from "../../utils/api/label_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import CustomButton from "../Button/customButton/CustomButton";

import {
  EditOutlined as EditIcon,
  Delete as DeleteIcon,
  UnfoldMoreOutlined as ExpandAllGlobleIcon,
  UnfoldLessOutlined as CollapseAllGlobleIcon,
  CreateNewFolderOutlined as CreateModuleicon,
  AddBoxRounded,
  Home
} from '@mui/icons-material';
import styles from '../../styles/component.module.scss';
import TestCasePopupsWrapper from "./TestCasePopupsWrapper";
import { useSnackbar } from "../../utils/useSnackbar";
import { findNodeById, scrollChildIntoView } from "../../utils/commonfunctions";
import { addModuleToTree, deleteNode, preprocessModules, updateIndividualCount, reorderSiblings } from "../../utils/moduleHelperFunction";
import { reorderModules } from "../../utils/api/module_apis";
import { getAddButtonText, getAddModalTitle, getEditModalTitle, getTerminologyForLevel, getDeleteCascadeText, canHaveChildren } from "../../utils/moduleTerminology";
import { getDeleteModalTitle, getDeleteConfirmMessage } from "../../utils/uiText";
import { ScaleEnum } from "../../enums/enumRegistry";
import ExcelExportExampleButton from "./ExcelExportExampleButton";
import ExcelImportExampleInput from "./ExcelImportExampleInput";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import ResourceNotFound from "../NotFound/ResourceNotFound";
import NotFoundResourceIcon from "../NotFound/NotFoundResourceIcon";

const DEFALT_MODULE_NAME = 'All Test Cases';
const DEFAULT_POPUP_DATA = {
  type: null,
  show: false,
  data: {},
};

// Function to get module path (same as in ModuleTreeView)
const getModulePathByLevel = (modules, moduleId) => {
  const findModulePath = (nodes, targetId) => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return [{ id: node.id, name: node.name, level: node.level }];
      }

      if (node.children) {
        const childPath = findModulePath(node.children, targetId);
        if (childPath) {
          return [
            ...(childPath.includes(node)
              ? []
              : [{ id: node.id, name: node.name, level: node.level }]),
            ...childPath,
          ];
        }
      }
    }
    return null;
  };

  const path = findModulePath(modules, moduleId);
  return path ? path : [];
};

function TestCaseView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();
  const theme = useTheme();

  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const activePage = useSelector((state) => state.navigation.activePage);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [modulePathForBreadcrumbs, setModulePathForBreadcrumbs] = useState([]);
  const [modules, setModules] = useState([]);

  // Current query AST (updated reactively via onQueryChange callback from TestCaseList)
  // This is the single source of truth for the current filter state
  const [currentQueryAST, setCurrentQueryAST] = useState(null);

  const moduleId = params.get("moduleId");
  const [idToNodeMap, setIdToNodeMap] = useState(new Map());

  const [isExpanded, setIsExpanded] = useState(false);
  const [splitSizes, setSplitSizes] = useState([23, 77]);
  const reorderAbortRef = useRef(null);

  // Responsive maxSize for Split component using custom breakpoints
  const isXxl = useMediaQuery(`(min-width:${breakpoints.xxl}px)`);
  const isXl = useMediaQuery(`(min-width:${breakpoints.xl}px)`);
  const isLg = useMediaQuery('(min-width:1200px)');
  const isMd = useMediaQuery('(min-width:900px)');

  const getResponsiveMaxSize = () => {
    if (isXxl) return [450, Infinity]; // xxl and above (1920px+)
    if (isXl) return [400, Infinity]; // xl and above (1440px+)
    if (isLg) return [350, Infinity]; // lg screens (1200px+)
    if (isMd) return [300, Infinity]; // md screens (900px+)
    return [280, Infinity]; // sm and below
  };

  async function addNewTestCaseModuleCount(moduleId, count = 1) {
    // If there's an active filter, refresh module counts from server to ensure accuracy
    if (currentQueryAST) {
      await fetchModulesData(currentQueryAST);
    } else {
      const updatedCount = idToNodeMap.get(moduleId)?.individualcount + count;
      const updatedModules = updateIndividualCount(idToNodeMap, moduleId, updatedCount, modules);
      setModules([...updatedModules]);
      setTreeData([...updatedModules]);
      const tempIdToNodeMap = preprocessModules(updatedModules);
      setIdToNodeMap(tempIdToNodeMap);
    }
  }

  function moveTestCaseModuleCount(moduleId, targetModuleId, count = 1) {
    const reducedCount = idToNodeMap.get(moduleId)?.individualcount - count;
    const reducedUpdatedModules = updateIndividualCount(idToNodeMap, moduleId, reducedCount, modules);
    let tempIdToNodeMap = preprocessModules(reducedUpdatedModules);
    const increasedCount = idToNodeMap.get(targetModuleId)?.individualcount + count;
    const addedUpdatedModules = updateIndividualCount(tempIdToNodeMap, targetModuleId, increasedCount, reducedUpdatedModules);
    setModules([...addedUpdatedModules]);
    setTreeData([...addedUpdatedModules]);
    tempIdToNodeMap = preprocessModules(addedUpdatedModules);
    setIdToNodeMap(tempIdToNodeMap);
  }

  async function deleteTestCaseModuleCount(moduleId, count = 1) {
    if (moduleId === "default") {
      // Use the current query AST (single source of truth from TestCaseList)
      await fetchModulesData(currentQueryAST);
    } else {
      const reducedCount = idToNodeMap.get(moduleId)?.individualcount - count;
      const reducedUpdatedModules = updateIndividualCount(idToNodeMap, moduleId, reducedCount, modules);
      setModules([...reducedUpdatedModules]);
      setTreeData([...reducedUpdatedModules]);
      const tempIdToNodeMap = preprocessModules(reducedUpdatedModules);
      setIdToNodeMap(tempIdToNodeMap);
    }

  }

  const [treeData, setTreeData] = useState([]);
  const [selected, setSelected] = useState(['default']);
  const [popUpData, setPopUpData] = useState(DEFAULT_POPUP_DATA);
  const [modulePaths, setModulePaths] = useState({});
  const { showSnackbar } = useSnackbar();
  const treeRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);
  const [labels, setLabels] = useState([]);
  const [isModulesLoading, setIsModulesLoading] = useState(true);
  const [moduleNotFound, setModuleNotFound] = useState(false);

  function getAllModuleCombinations(data) {
    const paths = {};

    function traverse(node, pathSoFar) {
      const newPath = [...pathSoFar, node.name];
      const pathString = newPath.join('/');
      paths[pathString] = node.id;

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => traverse(child, newPath));
      }
    }

    data.forEach(root => traverse(root, []));
    return paths;
  }

  const handleImportSuccess = async () => {
    // Use the current query AST (single source of truth from TestCaseList)
    await fetchModulesData(currentQueryAST);
    setRefreshKey(prev => prev + 1);
  }

  const fetchModulesData = useCallback(async (astToUse) => {
    setIsModulesLoading(true);
    try {
      // Pass AST to get filtered counts
      const result = await fetchModulesWithCount({ ast: astToUse });
      
      // Handle response: wrapper function returns array directly, or full response object
      let data = [];
      
      // Check if result is already an array (wrapper function returns just the array)
      if (Array.isArray(result)) {
        data = result;
      }
      // Check if result is an object with status and data
      else if (result?.status === STATUS.SUCCESS) {
        // API function returns { status, data: [...], message }
        data = Array.isArray(result.data) ? result.data : [];
      }
      // Check if result has data property that's an array
      else if (result?.data && Array.isArray(result.data)) {
        data = result.data;
      }
      // Check if result has modulesData property
      else if (result?.modulesData && Array.isArray(result.modulesData)) {
        data = result.modulesData;
      }
      
      if (data.length > 0) {
        setTreeData([...data]);
        setModules([...data]);
        const tempIdToNodeMap = preprocessModules(data);
        setIdToNodeMap(tempIdToNodeMap);
        setModulePaths(getAllModuleCombinations(data));
      } else {
        // Empty array - clear modules
        setTreeData([]);
        setModules([]);
        setIdToNodeMap(new Map());
        setModulePaths({});
      }
    } catch (error) {
      // On error, don't clear modules - keep existing state
      // Error is handled silently to prevent UI disruption
    } finally {
      setIsModulesLoading(false);
    }
  }, []);

  // Callback to handle query changes from TestCaseList
  // Updates local AST state (single source of truth) and refreshes module counts
  const handleQueryChange = useCallback(async (ast) => {
    // Store the current AST (single source of truth)
    setCurrentQueryAST(ast);
    // Refresh module counts with the new AST
    await fetchModulesData(ast);
  }, [fetchModulesData]);

  // Removed highlightedNodeIds calculation - no longer graying out modules based on filters
  // All modules are now shown at full opacity regardless of filter selection

  const fetchAllLabels = async () => {
    const response = await getAllLabels();
    if (response.status === STATUS.SUCCESS) {
      setLabels(response.data || []);
    }
  };


  useEffect(() => {
    fetchModulesData(null); // No filters on initial mount
    fetchAllLabels();
  }, []);

  useEffect(() => {
    if (isModulesLoading) return;
    if (!moduleId || moduleId === "default") {
      setModuleNotFound(false);
      return;
    }
    if (!idToNodeMap.has(moduleId)) {
      setModuleNotFound(true);
    } else {
      setModuleNotFound(false);
    }
  }, [moduleId, isModulesLoading, idToNodeMap]);

  // Note: Module count updates are now handled reactively via handleQueryChange callback
  // from TestCaseList. This useEffect is no longer needed since TestCaseList calls
  // onQueryChange whenever filters change, which updates currentQueryAST and refreshes counts.

  // Fetch modules when component mounts
  useEffect(() => {
    if (selectedProject?.id) {
      handleLeafNodeClick("default", DEFALT_MODULE_NAME)
    }
  }, [selectedProject?.id]);

  // Recalculate module path whenever modules change and a module is selected
  useEffect(() => {
    if (selectedModuleId && selectedModuleId !== "default") {
      const updatedModulePath = getModulePathByLevel(modules, selectedModuleId);
      setModulePathForBreadcrumbs(updatedModulePath);
    }
  }, [modules, selectedModuleId]);


  const parseCurrentSelectedNode = () => {
    if (moduleId) {
      handleLeafNodeClick(moduleId);
    }
  };

  useEffect(() => {
    setSelected([moduleId || "default"])
  }, [])

  useEffect(() => {
    parseCurrentSelectedNode();
  }, [moduleId]);

  const handleLeafNodeClick = (moduleId, moduleName) => {
    setSelectedModuleId(moduleId);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set("moduleId", moduleId);

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });

    if (moduleId === "default") {
      // Set a specific path for DEFALT_MODULE_NAME
      setModulePathForBreadcrumbs([
        {
          id: "default",
          name: DEFALT_MODULE_NAME,
          level: 0,
        },
      ]);
    } else {
      const modulePath = getModulePathByLevel(modules, moduleId);
      setModulePathForBreadcrumbs(modulePath);
    }
  };

  function handleBreadcrumbClick(id) {
    let { rURL, activePage = null } = (() => {
      if (id == "home") {
        return {
          rURL: DEFAULT_ROUTES.BASE_URL,
          activePage: "Projects",
        };
      }
      if (id == "project") {
        return {
          rURL: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}/test-cases`,
          activePage: "Test Cases",
        };
      }
    })();
    if (activePage) {
      dispatch(setActivePage(activePage));
    }
    navigate(rURL);
  }

  // Breadcrumb items
  const breadcrumbList = [
    {
      label: "Project",
      onClick: () => handleBreadcrumbClick("home"),
      // icon: (<Tooltip title="Home"><Home /></Tooltip>),
    },
    {
      label: selectedProject?.name || "Unknown Project",
    },
    {
      label: activePage
    },
  ];

  function handlePopupsClose() {
    setPopUpData(DEFAULT_POPUP_DATA);
  }

  async function handlePopupsConfirmation({ actionType, data }) {
    let responce = null;
    switch (actionType) {
      case 'addmodule':
        responce = await handleModuleAction('addRootNode', data.nodeName, data.context, data);
        if (responce) {
          const newNode = {
            id: responce?.id,
            parent_id: responce?.parent_id,
            name: responce?.name,
            level: responce?.level,
            individualcount: 0,
            totalcount: 0,
            children: [],
            context: responce?.context,
          };
          const updatedModules = addModuleToTree(idToNodeMap, newNode, modules);
          setModules([...updatedModules]);
          setTreeData([...updatedModules]);
          const tempIdToNodeMap = preprocessModules(updatedModules);
          setIdToNodeMap(tempIdToNodeMap);
          setSelected([String(newNode.id)]);
          handleLeafNodeClick(newNode.id, newNode.name);
          handlePopupsClose();
          if (responce?.id) {
            scrollChildIntoView({
              parentId: 'module-explorer',
              childId: responce?.id
            })
          }
        }
        break;
      case 'addChildNode':
        responce = await handleModuleAction('addNode', data.nodeName, data.context, data.parentId);
        if (responce?.status === 1) {
          const respData = responce?.data || {};
          const newNode = {
            id: respData?.id,
            parent_id: respData?.parent_id,
            name: respData?.name,
            level: respData?.level,
            individualcount: 0,
            totalcount: 0,
            children: [],
            context: respData?.context,
          };
          const updatedModules = addModuleToTree(idToNodeMap, newNode, modules);
          setModules([...updatedModules]);
          setTreeData([...updatedModules]);
          const tempIdToNodeMap = preprocessModules(updatedModules);
          setIdToNodeMap(tempIdToNodeMap);
          if (treeRef.current && respData?.level < 3) {
            treeRef.current.triggerExpandOneNode(respData?.parent_id || data.parentId);
          }
          setSelected([String(newNode.id)]);
          handleLeafNodeClick(newNode.id, newNode.name);
          handlePopupsClose();
          if (respData?.id) {
            scrollChildIntoView({
              parentId: 'module-explorer',
              childId: respData?.id
            })
          }
        }
        break;
      case 'editModule':
        await handleModuleAction('editNode', data.nodeName, data.context, null, data.data);
        handlePopupsClose();
        break;
      case 'deleteModule':
        responce = await handleModuleAction('deleteNode', null, null, null, data);
        if (responce) {
          const updatedModules = deleteNode(idToNodeMap, data, modules);
          setModules([...updatedModules]);
          setTreeData([...updatedModules]);
          const tempIdToNodeMap = preprocessModules(updatedModules);
          setIdToNodeMap(tempIdToNodeMap);
          // If the deleted node was selected, navigate to All Test Cases
          if (String(selectedModuleId) === String(data)) {
            setSelected(['default']);
            handleLeafNodeClick('default', DEFALT_MODULE_NAME);
          }
          // Note: refreshKey is already incremented in handleModuleAction
          // Refetch modules with current query AST to ensure counts/state are fresh
          await fetchModulesData(currentQueryAST);
          handlePopupsClose();
        }
        break;
    }
  }

  const handleReorder = useCallback(async (parentId, orderedIds) => {
    const snapshotModules = modules;
    const snapshotTree = treeData;
    const snapshotMap = idToNodeMap;

    try {
      const updatedModules = reorderSiblings(
        idToNodeMap,
        parentId,
        orderedIds,
        [...modules]
      );
      setModules([...updatedModules]);
      setTreeData([...updatedModules]);
      setIdToNodeMap(preprocessModules(updatedModules));
    } catch (error) {
      console.error('Failed to reorder modules locally', error);
      showSnackbar('Could not update folder order', 'error');
      return;
    }

    if (reorderAbortRef.current) {
      reorderAbortRef.current.abort();
    }
    const controller = new AbortController();
    reorderAbortRef.current = controller;

    const result = await reorderModules({
      parentId,
      orderedIds,
      signal: controller.signal,
    });

    if (result?.aborted) {
      return;
    }

    if (result?.status !== STATUS.SUCCESS) {
      setModules([...snapshotModules]);
      setTreeData([...snapshotTree]);
      setIdToNodeMap(snapshotMap);
      showSnackbar(result?.error || 'Could not save the new order', 'error');
    }
  }, [modules, treeData, idToNodeMap, showSnackbar]);

  function handleActionButtonClick(action, data) {
    switch (action) {
      case 'addmodule':
        setPopUpData({
          type: 'addmodule',
          show: true,
          title: 'Add Module',
          closePopup: handlePopupsClose,
          confirmationFunction: (nodeName, context) => { handlePopupsConfirmation({ actionType: 'addmodule', data: { nodeName, context } }) },
        })
        break;
      case 'addChildmodule':
        const parentNodeData = findNodeById(treeData, data);
        if (!parentNodeData) return;

        const modalTitle = getAddModalTitle(parentNodeData.level);
        setPopUpData({
          type: 'addChildmodule',
          show: true,
          title: modalTitle,
          data: data,
          closePopup: handlePopupsClose,
          confirmationFunction: (nodeName, context) => { handlePopupsConfirmation({ actionType: 'addChildNode', data: { parentId: data, nodeName, context } }) },
        })
        break;
      case 'editmodule':
        const editTitle = getEditModalTitle(data.level);
        setPopUpData({
          type: 'editModule',
          show: true,
          title: editTitle,
          data: data,
          closePopup: handlePopupsClose,
          confirmationFunction: (nodeName, context) => { handlePopupsConfirmation({ actionType: 'editModule', data: { data, nodeName, context } }) },
        })
        break;
      case 'deleteModule':
        const nodeToDelete = findNodeById(treeData, data);
        const deleteTitle = getDeleteModalTitle();
        const deleteMessage = nodeToDelete
          ? getDeleteConfirmMessage(getTerminologyForLevel(nodeToDelete.level).singular.toLowerCase(), 1, false, getDeleteCascadeText(nodeToDelete.level))
          : getDeleteConfirmMessage('item', 1, false);

        setPopUpData({
          type: 'deleteModule',
          show: true,
          data: data,
          title: deleteTitle,
          message: deleteMessage,
          closePopup: handlePopupsClose,
          confirmationFunction: () => handlePopupsConfirmation({ actionType: 'deleteModule', data: data })
        })
        break;
      default:
        // Invalid action type - silently ignore
        break;
    }
  }


  const handleModuleAction = async (action, nodeName, context, parentId, data) => {
    if (!nodeName?.trim() && action !== 'deleteNode') {
      showSnackbar("Please provide a valid module name", "error");
      return;
    }
    // Common function to check for duplicates
    const checkForDuplicates = (name, parentId = null, excludeId = null) => {
      if (parentId) {
        const parentNodeData = findNodeById(modules, parentId);
        if (parentNodeData) {
          const siblings = parentNodeData.children || [];
          return siblings.some(child =>
            child.name.toLowerCase() === name.toLowerCase() && child.id !== excludeId
          );
        }
      } else {
        return modules.some(module =>
          module.name.toLowerCase() === name.toLowerCase() &&
          module.level === 1 &&
          module.id !== excludeId
        );
      }
      return false;
    };

    try {
      if (action === "addRootNode") {
        if (checkForDuplicates(nodeName) || nodeName.toLowerCase() === DEFALT_MODULE_NAME.toLowerCase()) {
          showSnackbar("A module with the same name already exists", "error");
          return;
        }

        const newNodeData = { name: nodeName, parentId: null, projectId: selectedProject?.id, context, level: 1 };
        const response = await createModuleApiCall(newNodeData);
        if (response.status !== 1) throw new Error(response.message);
        // Increment refreshKey to trigger schema refresh
        setRefreshKey(prev => prev + 1);
        showSnackbar("Module added successfully", "success");
        return response.data;
      }

      if (action === "addNode") {
        if (!parentId) {
          showSnackbar("Please select a parent module", "error");
          return;
        }

        const parentNodeData = findNodeById(modules, parentId);
        if (!parentNodeData) {
          showSnackbar("Parent module not found", "error");
          return;
        }

        if (checkForDuplicates(nodeName, parentId)) {
          showSnackbar("A module with the same name already exists", "error");
          return;
        }

        const level = parentNodeData.level + 1;
        const newNodeData = { name: nodeName, parentId: parentId, projectId: selectedProject?.id, context, level };
        const response = await createModuleApiCall(newNodeData);
        if (response.status !== 1) throw new Error(response?.message || 'Failed to add module');
        // Increment refreshKey to trigger schema refresh
        setRefreshKey(prev => prev + 1);
        showSnackbar("Module added successfully", "success");
        return response;
      }

      if (action === "editNode") {
        if (!data?.id) {
          showSnackbar("Please select a module to edit", "error");
          return;
        }
        // Check for duplicate names in siblings
        let parent = null;
        const findParent = (nodes, nodeId) => {
          for (const node of nodes) {
            if (node.children?.some(child => child.id === nodeId)) {
              return node;
            }
            if (node.children?.length) {
              const found = findParent(node.children, nodeId);
              if (found) return found;
            }
          }
          return null;
        };

        if (data.level > 1) {
          parent = findParent(treeData, data.id);
          if (parent?.children) {
            const isDuplicate = parent.children
              .filter(child => child.id !== data.id)
              .some(child => child.name.toLowerCase() === nodeName.toLowerCase());

            if (isDuplicate) {
              showSnackbar("A module with the same name already exists", "error");
              return;
            }
          }
        } else {
          if (checkForDuplicates(nodeName, null, data.id)) {
            showSnackbar("A module with the same name already exists", "error");
            return;
          }
        }

        const response = await editModuleApiCall({ id: data.id, data: { name: nodeName, context } });
        if (response.status !== 1) throw new Error(response?.error || 'Failed to edit module');
        const updateNodeInTree = (nodes) => nodes.map((node) => {
          if (node.id === data.id) {
            return { ...node, name: nodeName, context };
          }
          if (node.children?.length) {
            return { ...node, children: updateNodeInTree(node.children) };
          }
          return node;
        });

        const updatedModules = updateNodeInTree(modules);
        setModules(updatedModules);
        setTreeData([...updatedModules]);
        const tempIdToNodeMap = preprocessModules(updatedModules);
        setIdToNodeMap(tempIdToNodeMap);

        
        setRefreshKey(prev => prev + 1);
        showSnackbar("Module updated successfully", "success");
      }

      if (action === "deleteNode") {
        if (!data) return;

        const response = await deleteModuleApiCall(data);
        if (response.status !== 1) throw new Error(response.error || 'Failed to delete Module');
        // Increment refreshKey to trigger schema refresh and filter pill cleanup
        setRefreshKey(prev => prev + 1);
        showSnackbar("Module deleted successfully", "success");
        return true;
      }

    } catch (error) {
      showSnackbar(error?.message || `Failed to ${action} module`, "error");
    }
  };


  const headerActionButtons = [
    {
      name: "Add Module",
      // tooltip: "Add New Module",
      icon: <PlusIcon className="icon" />,
      action: () => handleActionButtonClick('addmodule'),
      disabled: false,
    },
    {
      name: "Expand All",
      // tooltip: "Expand All Modules",
      icon: <ChevronDownIcon className="icon" />,
      action: () => treeRef.current?.triggerExpandAll(treeData),
      disabled: treeData.length === 0 || isExpanded,
    },
    {
      name: "Collapse All",
      // tooltip: "Collapse All Modules",
      icon: <ChevronUpIcon className="icon" />,
      action: treeRef.current?.triggerCollapseAll,
      disabled: treeData.length === 0 || !isExpanded,
    },
  ];

  const rowActionButtons = ((node) => {
    const addButtonText = getAddButtonText(node.level);

    return [
      ...(canHaveChildren(node.level) ? [{
        name: addButtonText.name,
        description: addButtonText.description,
        // tooltip: addButtonText.name,
        icon: <PlusIcon className="icon" />,
        action: (row) => handleActionButtonClick('addChildmodule', row.id),
        disabled: false,
      }] : []),
      {
        name: 'Edit',
        description: 'Modify settings',
        // tooltip: 'Edit',
        icon: <PencilIcon className="icon" />,
        action: (row) => handleActionButtonClick('editmodule', row),
        disabled: false,
      },
      {
        name: 'Delete',
        description: 'Remove permanently',
        // tooltip: 'Delete',
        icon: <TrashIcon className="icon" />,
        action: (row) => handleActionButtonClick('deleteModule', row.id),
        disabled: false,
      },
    ];
  })

  return (
    <Box className={styles['page-container']}>
      {/* Page Header - Sticky */}
      <Box>
        <PageHeader
          projectName={selectedProject?.name}
          title="Test Cases"
          description="Create, organize, and execute your test cases efficiently across all project modules"
          activePage={activePage}
          breadcrumbItems={breadcrumbList}
          actions={[
            <ExcelImportExampleInput modulePaths={modulePaths} onImportSuccess={handleImportSuccess} labelOptions={labels} />
          ]}
        />
      </Box>

      {/* Main Content - Takes remaining height */}
      <Box
        className={styles['cover-container-col']}
        sx={{
          p: 1,
          [`@media (min-width:${breakpoints.xl}px)`]: {
            px: 2,
            pt: 2,
          }
        }}
      >

        {/* Split view with thinner, resizable divider */}
        <>
          <Split
            sizes={splitSizes}
            minSize={260}
            maxSize={getResponsiveMaxSize()}
            gutterSize={18}
            gutterAlign="center"
            snapOffset={0}
            onDragEnd={(sizes) => setSplitSizes(sizes)}
            style={{ display: "flex", height: "100%" }}
            gutter={(index, direction) => {
              const gutterElement = document.createElement("div");
              gutterElement.className = `gutter gutter-${direction}`;
              return gutterElement;
            }}
          >
            {/* Left Panel: Module Explorer */}
            <Box>
              <FolderDrawerTreeView
                data={treeData}
                isDataLoading={isModulesLoading}
                highlightedNodeIds={null}
                title={selectedProject?.name || "Project Module"}
                moduleTitle={DEFALT_MODULE_NAME}
                titleLabel={"Project: " + selectedProject?.name || "<Project Name>"}
                onLeafNodeClick={handleLeafNodeClick}
                selected={selected}
                setSelected={setSelected}
                rowActionButtons={rowActionButtons}
                disableActions={false}
                headerActionButtons={headerActionButtons}
                disableHeaderActions={false}
                updateIsExpanded={(flag) => setIsExpanded(flag)}
                moduleEmptyPrompt={{
                  onAddModule: () => handleActionButtonClick('addmodule'),
                }}
                reorderable={true}
                onReorder={handleReorder}
                ref={treeRef}
              />
            </Box>

            {/* Right Panel: Test Case List */}
            <Box sx={{ height: "100%" }}>
              {moduleNotFound ? (
                <ResourceNotFound
                  icon={<NotFoundResourceIcon type="module" />}
                  title="Module not found"
                  message="This module may have been deleted or moved. Select another module from the tree or view all test cases."
                  actionLabel="View all test cases"
                  onAction={() => {
                    const sp = new URLSearchParams(location.search);
                    sp.set("moduleId", "default");
                    sp.delete("testCaseId");
                    navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
                    setModuleNotFound(false);
                  }}
                />
              ) : selectedModuleId ? (
                <TestCaseList
                  moduleId={selectedModuleId}
                  modulePath={modulePathForBreadcrumbs}
                  addNewTestCaseModuleCount={addNewTestCaseModuleCount}
                  moveTestCaseModuleCount={moveTestCaseModuleCount}
                  deleteTestCaseModuleCount={deleteTestCaseModuleCount}
                  refreshKey={refreshKey}
                  fetchLabelsForImportExcel={fetchAllLabels}
                  onLabelCreated={() => setRefreshKey(prev => prev + 1)}
                  modules={modules}
                  onQueryChange={handleQueryChange}
                />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                  <div>Select a module to view its test cases.</div>
                </Box>
              )}
            </Box>
          </Split>
        </>
      </Box>

      {/* Pop-up Modals */}
      <TestCasePopupsWrapper popUpData={popUpData} />
    </Box>
  );
}

export default TestCaseView;
